import { YearInReviewData } from "@ugg/shared/api/requests/summoner-profiles/year-in-review";

export function generateMonthLabels(): { categories: string[]; tickPositions: number[] } {
  return {
    categories: ["", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"],
    tickPositions: [0, 1 / 12, 2 / 12, 3 / 12, 4 / 12, 5 / 12, 6 / 12, 7 / 12, 8 / 12, 9 / 12, 10 / 12, 11 / 12],
  };
}

/**
 * Highcharts data point for LP graph
 */
export interface HighchartsLPDataPoint {
  x: number; // Year fraction (0-1 scale)
  y: number; // Absolute LP value (score from API)
  data: {
    tier: string;
    rank: string;
    lp: number;
    championId?: number;
    timestamp: number;
  };
  diff: number; // LP change from previous point
}

/**
 * Get the fractional position within the year (0-1)
 */
function getYearFraction(timestamp: number): number {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const startOfYear = new Date(year, 0, 1).getTime();
  const endOfYear = new Date(year, 11, 31, 23, 59, 59, 999).getTime();
  const yearDuration = endOfYear - startOfYear;

  return (timestamp - startOfYear) / yearDuration;
}

/**
 * Check if a timestamp is on a specific month and day
 */
function isDateOnMonthDay(timestamp: number, month: number, day: number): boolean {
  const date = new Date(timestamp);
  return date.getMonth() === month && date.getDate() === day;
}

interface ProcessedEntry {
  champion_id?: number;
  diff?: number;
  lp: number;
  match_creation_time?: string;
  match_id?: number;
  rank: string;
  score: number;
  tier: string;
  timestamp: number;
}

/**
 * Transform raw LP timeline data to Highcharts series format
 * Combines parsing, filtering, sorting, and mapping in a single pass
 * Uses 0-1 x-scale based on year fraction
 */
export function transformLPTimeline(lpTimeline: YearInReviewData["lp_timeline"]): HighchartsLPDataPoint[] {
  if (!lpTimeline || !Array.isArray(lpTimeline) || lpTimeline.length === 0) {
    return [];
  }

  // 1. Parse timestamps and filter invalid entries - O(n)
  const validEntries: ProcessedEntry[] = [];
  for (const entry of lpTimeline) {
    if (entry.score === undefined) continue;

    const timestamp = entry.match_creation_time ? new Date(entry.match_creation_time).getTime() : 0;
    if (timestamp <= 0) continue;

    validEntries.push({
      ...entry,
      timestamp,
    });
  }

  if (validEntries.length === 0) {
    return [];
  }

  // 2. Sort ONCE - O(n log n)
  validEntries.sort((a, b) => a.timestamp - b.timestamp);

  const firstEntry = validEntries[0];
  const lastEntry = validEntries[validEntries.length - 1];
  const year = new Date(firstEntry.timestamp).getFullYear();

  // 3. Add synthetic Jan 1 / Dec 31 points if needed
  const extendedEntries: ProcessedEntry[] = [...validEntries];

  if (!isDateOnMonthDay(firstEntry.timestamp, 0, 1)) {
    extendedEntries.unshift({
      timestamp: new Date(year, 0, 1).getTime(),
      tier: firstEntry.tier,
      rank: firstEntry.rank,
      lp: firstEntry.lp,
      score: firstEntry.score,
      champion_id: firstEntry.champion_id,
    });
  }

  if (!isDateOnMonthDay(lastEntry.timestamp, 11, 31)) {
    extendedEntries.push({
      timestamp: new Date(year, 11, 31).getTime(),
      tier: lastEntry.tier,
      rank: lastEntry.rank,
      lp: lastEntry.lp,
      score: lastEntry.score,
      champion_id: lastEntry.champion_id,
    });
  }

  // 4. Map to Highcharts format with 0-1 x-scale - O(n)
  return extendedEntries.map((entry, index) => {
    const prevScore = index > 0 ? extendedEntries[index - 1].score : entry.score;

    return {
      x: getYearFraction(entry.timestamp),
      y: entry.score,
      data: {
        tier: entry.tier,
        rank: entry.rank,
        lp: entry.lp,
        championId: entry.champion_id,
        timestamp: entry.timestamp,
      },
      diff: entry.score - prevScore,
    };
  });
}

/**
 * Calculate Y-axis bounds with padding
 */
export function calculateYAxisBounds(series: HighchartsLPDataPoint[]): {
  min: number;
  max: number;
} {
  if (!series || series.length === 0) return { min: 0, max: 4000 };

  const yValues = series.map((point) => point.y);
  const min = Math.min(...yValues);
  const max = Math.max(...yValues);

  // Add padding (100 LP above and below)
  return {
    min: Math.max(0, min - 100),
    max: max + 100,
  };
}
